import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button, Callout, Card, Loading } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { pinPadRules } from './pin-pad-rules.js';
import './sign-in.css';

/**
 * Signing in (SCOPE §12.5, §8.7 — decision 23).
 *
 * **Two questions, and the screen only ever asks one of them.**
 *
 * A till that has not been set up asks *which machine is this*: an email
 * address, then a six-digit code from the inbox. That happens once, and then
 * roughly four times a year.
 *
 * A till that has been set up asks *who is standing at it*: tap your name, type
 * your PIN. Recognition over recall (§2.2-3), and cheap enough that swapping
 * between sales stays attributed to the right person instead of everyone
 * sharing whoever signed in first. Or, once a store chooses it, just the PIN —
 * the pad §12.5 specifies, where the PIN alone says who you are (decision 138).
 *
 * There is no password anywhere. Nothing to choose badly, nothing to reuse from
 * another site, nothing to reset — and no shared one taped to the monitor.
 */

/**
 * @param storeName Which store this device is signing into. Hardcoding it was
 * fine while Sitzmark was the only tenant and wrong the moment there are two
 * (§12.3) — the sign-in screen has to name the shop whose subdomain you came in
 * on. Falls back to the product name when the tenant cannot be resolved, which
 * is itself the useful signal that something is misconfigured.
 */
export function SignIn({ storeName }: { storeName?: string | null }) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [renewing, setRenewing] = useState(false);

  const device = useQuery({
    queryKey: ['auth', 'device'],
    queryFn: () => api.auth.device.query(),
    staleTime: 60_000,
  });

  const enrolled = device.data?.enrolled === true;

  // How this store's pad works: names first or just the PIN, and how long a
  // PIN is. Only reachable on an enrolled till, which is the point — a list of
  // names beside a keypad is a credential-stuffing kit anywhere else.
  const pad = useQuery({
    queryKey: ['auth', 'pinPad'],
    queryFn: () => api.auth.pinPad.query(),
    enabled: enrolled,
    staleTime: 60_000,
  });

  const onSignedIn = () => {
    void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    // A sign-in records the PIN's length when it was not known. Without this
    // the pad keeps the old answer for a minute, and the next person to sign
    // in is still asked to press Sign in (decision 138).
    void queryClient.invalidateQueries({ queryKey: ['auth', 'pinPad'] });
  };

  const selected = pad.data?.people.find((employee) => employee.id === selectedId) ?? null;

  return (
    <div className="signin">
      <div className="signin__panel">
        <header className="signin__brand">
          <h1>{storeName ?? 'Shekko'}</h1>
          <p className="text-secondary">Point of sale</p>
        </header>

        {device.isPending ? (
          <Loading label="Checking this device" />
        ) : !enrolled ? (
          <EnrolDevice
            storeName={storeName ?? 'this store'}
            onEnrolled={() => {
              void queryClient.invalidateQueries({ queryKey: ['auth', 'device'] });
              onSignedIn();
            }}
          />
        ) : renewing ? (
          <EnrolDevice
            storeName={storeName ?? 'this store'}
            onEnrolled={() => {
              setRenewing(false);
              void queryClient.invalidateQueries({ queryKey: ['auth', 'device'] });
              onSignedIn();
            }}
            onCancel={() => setRenewing(false)}
          />
        ) : (
          <>
            {pad.isError ? (
              <Callout tone="danger" title="Cannot reach the store">
                {errorMessage(pad.error)}
              </Callout>
            ) : null}

            {/*
              The countdown (§2.2-9). The failure it exists for is precise: the
              token lapses on a Saturday in February, the owner is on a
              mountain, nobody can receive the enrolment email, and the shop
              cannot open. Two weeks of warning is enough that somebody with a
              mailbox is in the building at some point — and it is shown here,
              at sign-in, never mid-sale.
            */}
            {device.data?.enrolled === true && device.data.renewSoon ? (
              <Callout
                tone="warning"
                title={`This device needs setting up again in ${device.data.daysLeft} day${
                  device.data.daysLeft === 1 ? '' : 's'
                }`}
              >
                Do it now, while someone who can read the store email is here — not on the morning
                it stops working.
                <div className="signin__actions">
                  <Button variant="secondary" onClick={() => setRenewing(true)}>
                    Set this device up again
                  </Button>
                </div>
              </Callout>
            ) : null}

            {pad.data?.showsNames === false ? (
              <PinEntry
                title="Enter your PIN"
                length={pad.data.digits}
                send={(pin) => api.auth.pinOnlySignIn.mutate({ pin })}
                onSignedIn={onSignedIn}
                // Another till may have switched the pad back to names.
                onRefused={() =>
                  void queryClient.invalidateQueries({ queryKey: ['auth', 'pinPad'] })
                }
              />
            ) : selected === null ? (
              <Card title="Who is at the register?">
                <div className="signin__roster">
                  {pad.isPending ? (
                    <Loading label="Loading the roster" />
                  ) : (
                    pad.data?.people.map((employee) => (
                      <button
                        key={employee.id}
                        type="button"
                        className="signin__person"
                        onClick={() => setSelectedId(employee.id)}
                      >
                        {employee.displayName}
                      </button>
                    ))
                  )}
                </div>
              </Card>
            ) : (
              <PinEntry
                title={`PIN for ${selected.displayName}`}
                length={selected.pinDigits}
                send={(pin) => api.auth.pinSignIn.mutate({ employeeId: selected.id, pin })}
                onSignedIn={onSignedIn}
                onRefused={() => undefined}
                cancel={{
                  label: `Not ${selected.displayName}`,
                  onCancel: () => setSelectedId(null),
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Vouching for this machine (SCOPE §12.5).
 *
 * Two steps, one field each. The address is asked for first because it is the
 * only thing the person knows without looking anywhere; the code arrives in
 * their inbox and is typed straight in.
 *
 * The screen never says whether an address is on file — that is how you find
 * out who works at a shop, and it changes nothing a cashier would do
 * differently. If nothing arrives, the answer is the same either way: check the
 * spelling and ask again.
 */
function EnrolDevice({
  storeName,
  onEnrolled,
  onCancel,
}: {
  storeName: string;
  onEnrolled: () => void;
  onCancel?: () => void;
}) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [sent, setSent] = useState(false);
  /** No email to hand: an admin at another till approves this one instead. */
  const [askingAnotherTill, setAskingAnotherTill] = useState(false);

  const request = useMutation({
    mutationFn: () => api.auth.requestDeviceCode.mutate({ email: email.trim() }),
    onSuccess: () => setSent(true),
  });

  const enrol = useMutation({
    mutationFn: () =>
      api.auth.enrolDevice.mutate({
        email: email.trim(),
        code: code.trim(),
        deviceName: deviceName.trim() === '' ? 'Device' : deviceName.trim(),
      }),
    onSuccess: onEnrolled,
  });

  if (askingAnotherTill) {
    return <AskAnotherTill onEnrolled={onEnrolled} onBack={() => setAskingAnotherTill(false)} />;
  }

  if (!sent) {
    return (
      <Card title={`Set this device up for ${storeName}`}>
        <p className="text-secondary">
          We will email you a six-digit code. This happens once — after it, everyone signs in with
          their PIN.
        </p>
        <div className="field">
          <label className="field__label" htmlFor="enrol-email">
            Your work email
          </label>
          <input
            id="enrol-email"
            className="input"
            type="email"
            inputMode="email"
            autoFocus
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && email.includes('@')) request.mutate();
            }}
          />
        </div>

        {request.isError ? <Callout tone="danger">{errorMessage(request.error)}</Callout> : null}

        <div className="signin__actions">
          {onCancel === undefined ? null : (
            <Button variant="ghost" onClick={onCancel}>
              Not now
            </Button>
          )}
          <Button
            variant="primary"
            size="large"
            busy={request.isPending}
            disabledReason={email.includes('@') ? undefined : 'Enter your work email'}
            onClick={() => request.mutate()}
          >
            Email me a code
          </Button>
        </div>
        <p className="signin__alternative">
          <button type="button" className="link-button" onClick={() => setAskingAnotherTill(true)}>
            No email to hand? Ask an admin at another device
          </button>
        </p>
      </Card>
    );
  }

  return (
    <Card title="Type the code">
      {/*
        Deliberately not "we sent a code to …" — that would confirm the address
        belongs to somebody. It says what was done, not what was found.
      */}
      <p className="text-secondary">
        If {email.trim()} belongs to someone here, a six-digit code is on its way. It expires in ten
        minutes and works once.
      </p>

      <div className="field">
        <label className="field__label" htmlFor="enrol-code">
          Code
        </label>
        <input
          id="enrol-code"
          className="input code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && code.length === 6) enrol.mutate();
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="enrol-name">
          What is this device called?
        </label>
        <input
          id="enrol-name"
          className="input"
          maxLength={60}
          placeholder="Front counter"
          value={deviceName}
          onChange={(event) => setDeviceName(event.target.value)}
        />
        <p className="field__hint">So a manager can tell it apart in Settings, and revoke it.</p>
      </div>

      {enrol.isError ? <Callout tone="danger">{errorMessage(enrol.error)}</Callout> : null}

      <div className="signin__actions">
        <Button
          variant="ghost"
          busy={request.isPending}
          onClick={() => {
            setCode('');
            request.mutate();
          }}
        >
          Send another
        </Button>
        <Button
          variant="primary"
          size="large"
          busy={enrol.isPending}
          disabledReason={code.length === 6 ? undefined : 'The code is six digits'}
          onClick={() => enrol.mutate()}
        >
          Set up this device
        </Button>
      </div>
    </Card>
  );
}

/**
 * No email to hand: ask an admin at a till that is already set up (SCOPE
 * §12.5, decision 137 — the required fallback). This till shows a code; the
 * admin types it in at Settings → Tills; this till notices, and is trusted.
 *
 * The code on the screen is not what makes it trusted — the page also holds a
 * secret nobody can read off the screen, and only it can finish. So someone
 * reading the six digits over a shoulder cannot walk off with a till.
 */
function AskAnotherTill({ onEnrolled, onBack }: { onEnrolled: () => void; onBack: () => void }) {
  const [deviceName, setDeviceName] = useState('');
  const [asked, setAsked] = useState<{ code: string; secret: string } | null>(null);
  const [ranOut, setRanOut] = useState(false);

  const ask = useMutation({
    mutationFn: () =>
      api.auth.requestDeviceApproval.mutate({
        deviceName: deviceName.trim() === '' ? 'Device' : deviceName.trim(),
      }),
    onSuccess: (issued) => {
      setRanOut(false);
      setAsked({ code: issued.code, secret: issued.secret });
    },
  });

  // Ask every two seconds whether an admin has approved it yet.
  useEffect(() => {
    if (asked === null) return;
    let stopped = false;
    const timer = window.setInterval(() => {
      void api.auth.claimDeviceApproval
        .mutate({ secret: asked.secret })
        .then((state) => {
          if (stopped) return;
          if (state.status === 'enrolled') {
            window.clearInterval(timer);
            onEnrolled();
          } else if (state.status === 'gone') {
            window.clearInterval(timer);
            setAsked(null);
            setRanOut(true);
          }
        })
        .catch(() => undefined);
    }, 2_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [asked, onEnrolled]);

  if (asked !== null) {
    return (
      <Card title="Ask an admin to approve this device">
        <p className="text-secondary">
          On a device that is already set up, an admin opens Settings → Devices → Approve a new
          device, and types this code:
        </p>
        <p className="signin__pairing code" aria-label={`Approval code ${asked.code}`}>
          {asked.code.slice(0, 3)} {asked.code.slice(3)}
        </p>
        <p className="text-secondary" role="status">
          Waiting for approval… This device finishes setting itself up as soon as it is approved.
          The code runs out in ten minutes.
        </p>
        <div className="signin__actions">
          <Button
            variant="ghost"
            onClick={() => {
              setAsked(null);
              onBack();
            }}
          >
            Use an emailed code instead
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Ask an admin at another device">
      <p className="text-secondary">
        For when the emailed code cannot arrive — the internet is down, or you have no work email
        yet. An admin approves this device from one that is already set up.
      </p>
      {ranOut ? (
        <Callout tone="warning">That code ran out before it was approved. Ask again.</Callout>
      ) : null}
      <div className="field">
        <label className="field__label" htmlFor="approval-name">
          What is this device called?
        </label>
        <input
          id="approval-name"
          className="input"
          maxLength={60}
          autoFocus
          placeholder="Front counter"
          value={deviceName}
          onChange={(event) => setDeviceName(event.target.value)}
        />
        <p className="field__hint">The admin sees this name when they approve it.</p>
      </div>
      {ask.isError ? <Callout tone="danger">{errorMessage(ask.error)}</Callout> : null}
      <div className="signin__actions">
        <Button variant="ghost" onClick={onBack}>
          Use an emailed code instead
        </Button>
        <Button variant="primary" size="large" busy={ask.isPending} onClick={() => ask.mutate()}>
          Ask for approval
        </Button>
      </div>
    </Card>
  );
}

/**
 * The keypad, with a name or without (decision 138).
 *
 * @param length The PIN's length when it is known — the person's own on the pad
 * with names, the store's on the pad without. Null waits for Sign in rather
 * than guessing: the pad once signed in on the fourth digit whatever the PIN,
 * so a longer one could never be typed.
 */
function PinEntry({
  title,
  length,
  send,
  onSignedIn,
  onRefused,
  cancel,
}: {
  title: string;
  length: number | null;
  send: (pin: string) => Promise<unknown>;
  onSignedIn: () => void;
  onRefused: () => void;
  cancel?: { label: string; onCancel: () => void };
}) {
  const [pin, setPin] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const rules = pinPadRules(length);

  const signIn = useMutation({
    mutationFn: send,
    onSuccess: onSignedIn,
    onError: () => {
      setPin('');
      onRefused();
    },
  });

  // Keyboard-always (§2-1): the pad takes focus so digits can simply be typed.
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const submit = (value: string) => {
    if (rules.submitReason(value.length) === undefined) signIn.mutate(value);
  };

  const press = (digit: string) => {
    if (signIn.isPending || pin.length >= rules.maxDigits) return;
    const next = pin + digit;
    setPin(next);
    // On the last digit when the length is known — no Enter needed on the
    // common path. When it is not, Sign in says when it is finished.
    if (rules.submitsAt !== null && next.length === rules.submitsAt) submit(next);
  };

  return (
    <Card title={title}>
      <div
        className="pinpad"
        ref={containerRef}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (/^\d$/.test(event.key)) {
            event.preventDefault();
            press(event.key);
          } else if (event.key === 'Backspace') {
            event.preventDefault();
            setPin((current) => current.slice(0, -1));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            submit(pin);
          } else if (event.key === 'Escape' && cancel !== undefined) {
            event.preventDefault();
            cancel.onCancel();
          }
        }}
      >
        <div className="pinpad__display" aria-live="polite">
          <span className="visually-hidden">{pin.length} digits entered</span>
          {Array.from({ length: rules.places(pin.length) }, (_unused, index) => (
            <span
              key={index}
              className={`pinpad__dot${index < pin.length ? ' pinpad__dot--filled' : ''}`}
            />
          ))}
        </div>

        {/* The server's words: a locked till says it is locked, not "not recognized". */}
        {signIn.isError ? <Callout tone="danger">{errorMessage(signIn.error)}</Callout> : null}
        {rules.submitsAt === null && !signIn.isError ? (
          <p className="pinpad__hint text-secondary">Type your PIN, then Sign in.</p>
        ) : null}

        {/* Disabled only while a PIN is being checked, and says so (aria-busy) as the Button primitive does. */}
        <div className="pinpad__keys">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
            <button
              key={digit}
              type="button"
              className="pinpad__key num"
              onClick={() => press(digit)}
              disabled={signIn.isPending}
              aria-busy={signIn.isPending || undefined}
            >
              {digit}
            </button>
          ))}
          <button
            type="button"
            className="pinpad__key pinpad__key--muted"
            onClick={() => setPin('')}
            disabled={signIn.isPending}
            aria-busy={signIn.isPending || undefined}
          >
            Clear
          </button>
          <button
            type="button"
            className="pinpad__key num"
            onClick={() => press('0')}
            disabled={signIn.isPending}
            aria-busy={signIn.isPending || undefined}
          >
            0
          </button>
          <button
            type="button"
            className="pinpad__key pinpad__key--muted"
            onClick={() => setPin((current) => current.slice(0, -1))}
            disabled={signIn.isPending}
            aria-busy={signIn.isPending || undefined}
          >
            ⌫
          </button>
        </div>

        <div className="pinpad__footer">
          {cancel === undefined ? (
            <span />
          ) : (
            <Button variant="ghost" onClick={cancel.onCancel}>
              {cancel.label}
            </Button>
          )}
          <Button
            variant="primary"
            busy={signIn.isPending}
            disabledReason={rules.submitReason(pin.length)}
            onClick={() => submit(pin)}
          >
            {signIn.isPending ? 'Checking…' : 'Sign in'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
